import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import SigningProgressChart from './SigningProgressChart';

function Sidebar({ isOpen, onClose }) {
  const location = useLocation();
  const isHomeActive = location.pathname === '/' || location.pathname === '';
  const isProfileActive = location.pathname === '/profile' || location.pathname.startsWith('/profile');

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      <div
        className={`
          fixed inset-y-0 left-0 z-30 w-64 bg-white/95 backdrop-blur-xl shadow-xl border-r border-gray-200
          lg:translate-x-0 lg:shadow-none
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          <div className="px-4 h-[76px] flex items-center border-b border-gray-100">
            <div className="grid leading-tight">
              <img
                src="/icompaas-logo-for-light-backgrounds.svg"
                alt="iCompaas"
                className="h-6"
              />
              {/* Indent to the wordmark: kit text starts at 4/3 × logo height (h-6 → pl-8). */}
              <div className="-mt-0.5 pl-8">
                <h1 className="text-xs font-medium text-gray-600">Compliance Hub</h1>
                <p className="text-xs text-gray-500">Employee Portal</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 py-4 space-y-0.5 overflow-y-auto">
            <Link
              to="/"
              onClick={onClose}
              className={`
                group flex items-center py-3 px-4 text-sm font-normal transition-all duration-200 relative
                ${
                  isHomeActive
                    ? 'text-gray-900'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
            >
              {isHomeActive && (
                <motion.div
                  layoutId="activeIndicator"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  className="absolute right-0 top-2 bottom-2 w-1 bg-blue-500 rounded-l"
                />
              )}
              <div className="mr-3 flex-shrink-0 transition-colors duration-200">
                {isHomeActive ? (
                  <svg
                    className="h-5 w-5 transition-colors duration-200 text-blue-600"
                    height="512"
                    viewBox="0 0 512 512"
                    width="512"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill="currentColor"
                      d="m497.52 214.92-208.36-201.44c-18.59-17.98-47.73-17.98-66.33 0l-208.35 201.44c-13.98 13.52-18.28 33.87-10.96 51.85s24.64 29.6 44.12 29.6h6.38v148.25c0 37.16 30.35 67.39 67.66 67.39h268.63c37.31 0 67.66-30.23 67.66-67.39v-148.25h6.38c19.48 0 36.8-11.62 44.12-29.6 7.33-17.98 3.03-38.33-10.95-51.85zm-188.9 262.14h-105.24v-110.31c0-17.89 14.61-32.44 32.58-32.44h40.09c17.96 0 32.58 14.55 32.58 32.44v110.31zm167.35-223.42c-.95 2.34-3.99 7.79-11.61 7.79h-23.92c-9.69 0-17.54 7.82-17.54 17.47v165.72c0 17.89-14.61 32.44-32.58 32.44h-46.61v-110.31c0-37.16-30.35-67.39-67.66-67.39h-40.09c-37.31 0-67.66 30.23-67.66 67.39v110.31h-46.61c-17.96 0-32.58-14.55-32.58-32.44v-165.72c0-9.65-7.85-17.47-17.54-17.47h-23.93c-7.62 0-10.66-5.45-11.61-7.79s-2.58-8.36 2.88-13.64l208.36-201.45c2.45-2.37 5.59-3.55 8.73-3.55s6.28 1.18 8.73 3.55l208.36 201.44c5.46 5.29 3.83 11.31 2.88 13.65z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5 transition-colors duration-200 text-gray-400 group-hover:text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                )}
              </div>
              <span className="tracking-wide">Dashboard</span>
            </Link>
            <Link
              to="/profile"
              onClick={onClose}
              className={`
                group flex items-center py-3 px-4 text-sm font-normal transition-all duration-200 relative
                ${
                  isProfileActive
                    ? 'text-gray-900'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
            >
              {isProfileActive && (
                <motion.div
                  layoutId="activeIndicator"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  className="absolute right-0 top-2 bottom-2 w-1 bg-blue-500 rounded-l"
                />
              )}
              <div className="mr-3 flex-shrink-0 transition-colors duration-200">
                  <svg
                    className={`h-5 w-5 transition-colors duration-200 ${isProfileActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}
                    clipRule="evenodd"
                    fillRule="evenodd"
                    strokeLinejoin="round"
                    strokeMiterlimit="1.414"
                    viewBox="0 0 32 32"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path id="user-profile-man" d="m0 0h32v32h-32z" fill="none"/>
                    <path
                      d="m23.112 15.884c.352.045.622.278.896.57 1.879 1.999 2.964 4.686 2.982 7.455.012 1.865-1.91 3.224-3.885 3.951-3.191 1.174-6.717 1.353-10.137.936-2.251-.274-4.549-.746-6.413-2.158-.848-.642-1.552-1.585-1.545-2.728.018-2.821 1.136-5.543 3.075-7.555.493-.512 1.42-.576 1.754.135.203.432.019.902-.354 1.292-1.53 1.618-2.434 3.819-2.475 6.029-.016.83.844 1.379 1.65 1.779 2.079 1.032 4.478 1.335 6.847 1.383 2.584.052 5.23-.204 7.557-1.253.661-.298 1.341-.645 1.753-1.254.226-.333.179-.807.143-1.241-.174-2.106-1.128-4.135-2.638-5.638 0 0-.231-.27-.278-.532-.106-.591.423-1.198 1.068-1.171zm-7.067-12.884c2.558.024 5.047 1.56 6.202 3.848.961 1.904.982 4.255.056 6.177-1.513 3.139-5.565 4.851-8.985 3.428-2.232-.929-3.908-3.086-4.244-5.483-.307-2.192.499-4.51 2.102-6.038 1.272-1.213 2.994-1.915 4.779-1.932zm-.077 1.998c-2.281.022-4.404 1.755-4.862 3.989-.334 1.629.212 3.415 1.405 4.58 1.379 1.347 3.567 1.793 5.376 1.053 2.111-.864 3.452-3.313 3.019-5.572-.425-2.215-2.498-3.998-4.809-4.049-.043-.001-.086-.001-.129-.001z"
                      fillRule="nonzero"
                      fill="currentColor"
                    />
                  </svg>
              </div>
              <span className="tracking-wide">User Profile</span>
            </Link>
          </nav>

          {/* Signing Progress Chart at bottom */}
          <SigningProgressChart />
        </div>
      </div>
    </>
  );
}

export default Sidebar;